import React, { useEffect, useRef, useState } from 'react'
import { Input, Svg, SimpleActionMenuItemProps } from 'ui'
import { Flex, Text } from 'theme-ui'
import { LoginSignUpProps, SessionDuration } from './LoginSignUpForm.interface'
import { Link } from 'gatsby'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import PasswordInput from '../PasswordInput'
import SimpleDropdown from 'ui/Dropdown/SimpleDropdown'
import {
  StaticPageBody,
  StaticPageHeader,
  StaticPageWrapper,
  TitleSection,
  MainContentContainer,
} from 'components/NewStaticPage'
import CheckIcon from 'images/analytics/check.svg'
import { LoginSignUpButton } from 'components/LoginSignUpForm/LoginSignUpButton'
import useGetUserState from 'store/api/user/useGetUserState'
import DividerWithText from 'ui/DividerWithText'
import { LoginSignUpGoogleButton } from 'components/LoginSignUpForm/LoginSignUpGoogleButton'
import { setPriceInterval, setSelectedProductPk } from 'store/billing/products'
import { CommonPriceInterval, ProductType } from 'store/api/billing/payments.interface'
import { getPriceInterval } from 'components/Plans/SelectPlan/SelectPlan'
import { useGetProductsQuery } from 'store/api/billing'

interface IntervalItem {
  ttl: SessionDuration
  text: string
}

const sessionDurations: IntervalItem[] = [
  {
    ttl: SessionDuration.HOUR,
    text: '1 hour',
  },
  {
    ttl: SessionDuration.DAY,
    text: '1 day',
  },
  {
    ttl: SessionDuration.MONTH,
    text: '1 month',
  },
  {
    ttl: SessionDuration.YEAR,
    text: '1 year',
  },
]

export default function LoginForm({
  error,
  handleSubmit,
  handleUpdate,
  form,
  isLoading,
  isSsoLoginLoading,
}: LoginSignUpProps): React.ReactElement {
  const emailInputRef = useRef<HTMLInputElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const sessionDurationDropdownHideRef = useRef<() => void>()

  const emailExists: boolean = form.email.length > 0
  const passwordExists: boolean = form.password.length > 0
  const isLoginButtonEnabled = emailExists && passwordExists

  const [showPassword, setShowPassword] = useState(false)
  const shouldRememberMe = useAppSelector(
    s => s.session.wasRememberMeSelected === undefined || s.session.wasRememberMeSelected,
  )
  const dispatch = useAppDispatch()
  const { data: productsData } = useGetProductsQuery({})
  const fullControl = productsData?.products?.find(x => x.name === ProductType.FULL)
  const shouldForceTrial = useAppSelector(s => s.tracking.shouldForceTrial)
  const { ssoLogin } = useGetUserState()

  // fix for iOS Chrome oddly scrolling the page down when reloading the page with an input field focused
  useEffect(() => {
    scrollTo(0, 0)
  }, [])

  useEffect(() => {
    // fix an issue with scrolling to top for some android devices (landscape mode)
    error && (document.body.scrollTop = document.documentElement.scrollTop = 0)
  }, [error])

  useEffect(() => {
    // artificially trigger a change event once on first render to handle race conditions
    // with automatic input from things like password managers
    handleUpdate({ name: 'email', value: emailInputRef.current?.value || '' })
    handleUpdate({
      name: 'password',
      value: passwordInputRef.current?.value || '',
    })
  }, [handleUpdate, shouldRememberMe])

  const sessionDurationMenuItems: SimpleActionMenuItemProps[] = sessionDurations.map(duration => ({
    isSelected: sessionDurations.find(interval => interval.ttl === form.ttl) === duration,
    isBackgroundHighlighted: true,
    ariaLabel: `${duration.text} option`,
    onClick: () => {
      handleUpdate({
        name: 'ttl',
        value: duration.ttl,
      })
      sessionDurationDropdownHideRef.current?.()
    },
    buttonContent: <SessionDurationItem title={duration.text} />,
    children: (
      <Flex sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
        <SessionDurationItem title={duration.text} />
        {sessionDurations.find(interval => interval.ttl === form.ttl) === duration && (
          <Svg
            svg={CheckIcon}
            sx={{ flexShrink: 0, fill: 'white', opacity: 0.5 }}
            descriptiveText={`${duration.text} duration selected`}
          />
        )}
      </Flex>
    ),
    tabIndex: 0,
  }))

  return (
    <StaticPageWrapper>
      <StaticPageHeader />
      <StaticPageBody>
        <TitleSection error={error} isLoading={isLoading}>
          Log in
        </TitleSection>
        <MainContentContainer>
          <Flex
            as="form"
            aria-labelledby="login-heading"
            className="b2b-input-autofill"
            sx={{
              width: ['100%', '63rem'],
              position: 'relative',
              height: 'auto',
              py: '4rem',
              px: ['1.6rem', '4rem'],
              justifyContent: 'center',
            }}
            data-testid="login-form"
            onSubmit={(e: React.FormEvent): void => handleSubmit?.(e)}
          >
            <Flex
              sx={{
                flexDirection: 'column',
                gap: '2.4rem',
                width: ['100%', '48rem'],
              }}
            >
              <LoginSignUpGoogleButton
                onClick={() => {
                  if (fullControl?.PK) {
                    dispatch(setSelectedProductPk(fullControl.PK))

                    dispatch(
                      setPriceInterval(
                        shouldForceTrial
                          ? CommonPriceInterval.TRIAL
                          : getPriceInterval(fullControl),
                      ),
                    )
                  }
                }}
              />
              <DividerWithText
                text="OR"
                textVariant="size17Weight500Line138"
                sx={{
                  gap: '1.6rem',
                  span: {
                    color: 'white',
                  },
                }}
              />
              <Input
                label="Email"
                id="email"
                type="email"
                name="email"
                aria-label="Email"
                aria-required="true"
                data-testid="login-email"
                onChange={(e): void => handleUpdate(e.target)}
                hasText={emailExists}
                ref={emailInputRef}
              />
              <Flex sx={{ position: 'relative' }}>
                <PasswordInput
                  label="Password"
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  aria-label="Password"
                  aria-required="true"
                  data-testid="login-password"
                  onChange={(e): void => handleUpdate(e.target)}
                  ref={passwordInputRef}
                  shouldShowShowPasswordButton
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  sxShowPasswordStyle={{
                    bottom: '1.2rem',
                    right: '1.2rem',
                  }}
                />
                <Flex
                  // @ts-ignore
                  to="/forgot-password"
                  as={Link}
                  sx={{
                    position: 'absolute',
                    right: '0rem',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    opacity: 0.7,
                    textDecoration: 'none',
                    ':hover': {
                      opacity: 1,
                    },
                  }}
                  color="aliceBlue"
                  tabIndex={0}
                >
                  Forgot Password?
                </Flex>
              </Flex>
              <SimpleDropdown
                testId="remember-me-dropdown"
                items={sessionDurationMenuItems}
                hideRef={sessionDurationDropdownHideRef}
                label="Remember Me"
                ariaLabel="Remember Me Dropdown"
              />
              <LoginSignUpButton
                data-testid="login-button"
                ariaLabel="Log In Button"
                isLoading={isLoading}
                disabled={!isLoginButtonEnabled}
              >
                Log in
              </LoginSignUpButton>
              <LoginSignUpButton
                data-testid="sso-login-button"
                ariaLabel="Log in with SSO Button"
                isLoading={!!isSsoLoginLoading}
                disabled={!emailExists}
                onClick={() =>
                  ssoLogin({
                    email: form.email,
                  })
                }
                isSubmitButton={false}
              >
                Log in with SSO
              </LoginSignUpButton>
            </Flex>
          </Flex>
        </MainContentContainer>
      </StaticPageBody>
    </StaticPageWrapper>
  )
}

const SessionDurationItem = ({ title }: { title: string }) => {
  return (
    <Text
      variant="size15Weight600"
      sx={{
        textAlign: 'left',
        pr: '2.4rem',
      }}
    >
      {title}
    </Text>
  )
}
